import { X } from 'lucide-react'
import * as React from 'react'
import { useLocation } from 'react-router'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '../../utils/cn'

function Drawer({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerOverlay({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className
      )}
      {...props}
    />
  )
}

function DrawerContent({
  className,
  children,
  hideOverlay = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  hideOverlay?: boolean
}) {
  return (
    <DrawerPortal data-slot="drawer-portal">
      {!hideOverlay && <DrawerOverlay />}
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          'group/drawer-content bg-background fixed z-50 flex h-auto flex-col',
          'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:border-b',
          'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh]',
          'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm',
          'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:sm:max-w-sm',
          className
        )}
        {...props}
      >
        <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        'flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left',
        className
      )}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="drawer-footer" className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
  )
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

// Don't let a click on a global comms control (the floating Aircall/Dialpad
// launchers and call panels, tagged `data-comms-control`) dismiss the drawer —
// they live outside the drawer's portal, so vaul would otherwise treat the
// click as an outside-interaction and close it.
function isCommsControlInteraction(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[data-comms-control]') !== null
}

function DrawerWithCloseButton({
  children,
  handleClose,
  title,
  open,
  onOpenChange,
  className,
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  handleClose: () => void
  title: string | React.ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  // The fixed 50px comms bar only renders on internal pages (InternalLayout).
  // There, stop the drawer 50px above the bottom so the bar and its phone
  // controls stay visible/clickable instead of being covered by the drawer.
  const offsetForCommsBar = useLocation().pathname.includes('/internal')
  return (
    <Drawer direction="right" open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        // Defaults to a slim panel. Pass `className` to control width/max-width
        // (e.g. `w-[95vw]! sm:max-w-[1400px]!`); it replaces the default so the
        // two `max-w` important classes don't collide in tailwind-merge.
        className={cn(
          'overflow-visible border-none',
          offsetForCommsBar && 'bottom-[50px]!',
          className ?? 'sm:max-w-lg!'
        )}
        style={{ boxShadow: '-4px 0 6px 0 rgba(0, 0, 0, 0.05)' }}
        hideOverlay
        onPointerDownOutside={(e) => {
          if (isCommsControlInteraction(e.detail.originalEvent.target)) e.preventDefault()
        }}
        onInteractOutside={(e) => {
          if (isCommsControlInteraction(e.detail.originalEvent.target)) e.preventDefault()
        }}
      >
        <DrawerHeader className="p-0">
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
        </DrawerHeader>

        <div
          className="bg-background absolute top-5 -left-5 z-100 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full"
          onClick={handleClose}
          style={{ boxShadow: '-4px 0 6px 0 rgba(0, 0, 0, 0.05)' }}
        >
          <X className="text-neutral-400" />
        </div>

        <div className="h-full overflow-y-auto px-4 py-6 md:px-8 md:py-10">{children}</div>
      </DrawerContent>
    </Drawer>
  )
}

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  DrawerWithCloseButton,
}
